import { listRowHeight } from "../../ui/kit/index.ts";

/**
 * The places panel's model (ADR-100's note of 2026-10-14, decision 5): the known places grouped by kind under small
 * headings, narrowed by a filter, laid out in rows of fixed heights so the panel draws only the rows in view however
 * many places there are. A kind the table does not name joins after the named ones in the order it first appears, so a
 * later kind needs a heading word and nothing here. Pure: entries in, rows out.
 */

/**
 * The panel shows its filter field only above this many places.
 */
export const FILTER_AFTER = 12;

/**
 * A group's heading height in logical pixels.
 */
export const HEADING_HEIGHT = 14;

/**
 * A place's row height in logical pixels: a name with its distance under it.
 */
export const ROW_HEIGHT = listRowHeight({ glyph: false, sub: true, trailing: false });

/**
 * How many rows beyond the viewport are drawn on each side.
 */
export const OVERSCAN = 3;

/**
 * What the panel reads of a place.
 */
export interface PanelEntry {
  readonly key: string;
  readonly kind: string;
  readonly name: string;
}

/**
 * A group's heading.
 */
export interface PanelHeading {
  readonly type: "heading";
  readonly kind: string;
  readonly count: number;
  readonly top: number;
  readonly height: number;
}

/**
 * A place's row.
 */
export interface PanelRow<T extends PanelEntry> {
  readonly type: "place";
  readonly entry: T;
  readonly top: number;
  readonly height: number;
}

/**
 * A heading or a row.
 */
export type PanelItem<T extends PanelEntry> = PanelHeading | PanelRow<T>;

/**
 * The panel's rows.
 */
export interface PanelModel<T extends PanelEntry> {
  readonly items: readonly PanelItem<T>[];
  readonly height: number;
  readonly total: number;
  readonly matching: number;
  readonly order: readonly string[];
  readonly index: ReadonlyMap<string, PanelRow<T>>;
}

/**
 * Whether the panel shows its filter field.
 *
 * @param total The number of known places.
 * @returns True above `FILTER_AFTER`.
 */
export function showsFilter(total: number): boolean {
  return total > FILTER_AFTER;
}

/**
 * Builds the panel: the places that match the filter, grouped by kind, each group under its heading.
 *
 * @param entries Every known place, nearest first.
 * @param filter The text typed, matched against names without regard to case or the spaces round it.
 * @param kinds The kinds in the order their groups are shown.
 * @returns The model.
 */
export function buildPanel<T extends PanelEntry>(entries: readonly T[], filter: string, kinds: readonly string[]): PanelModel<T> {
  const wanted = filter.trim().toLowerCase();
  const groups = new Map<string, T[]>(kinds.map((kind) => [kind, []]));
  let matching = 0;
  for (const entry of entries) {
    if (wanted !== "" && !entry.name.toLowerCase().includes(wanted)) {
      continue;
    }
    matching += 1;
    const group = groups.get(entry.kind);
    if (group === undefined) {
      groups.set(entry.kind, [entry]);
    } else {
      group.push(entry);
    }
  }
  const items: PanelItem<T>[] = [];
  const order: string[] = [];
  const index = new Map<string, PanelRow<T>>();
  let top = 0;
  for (const [kind, members] of groups) {
    if (members.length === 0) {
      continue;
    }
    items.push({ type: "heading", kind, count: members.length, top, height: HEADING_HEIGHT });
    top += HEADING_HEIGHT;
    for (const entry of members) {
      const row: PanelRow<T> = { type: "place", entry, top, height: ROW_HEIGHT };
      items.push(row);
      index.set(entry.key, row);
      order.push(entry.key);
      top += ROW_HEIGHT;
    }
  }
  return { items, height: top, total: entries.length, matching, order, index };
}

/**
 * The rows that touch a scrolled viewport, and `overscan` more on each side, found by bisection.
 *
 * @param model The model.
 * @param scrollTop How far the panel is scrolled, in logical pixels.
 * @param viewport The viewport's height.
 * @param overscan How many items beyond it on each side; `OVERSCAN` unless given.
 * @returns The items to draw.
 */
export function visibleItems<T extends PanelEntry>(model: PanelModel<T>, scrollTop: number, viewport: number, overscan: number = OVERSCAN): readonly PanelItem<T>[] {
  const items = model.items;
  let low = 0;
  let high = items.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    const item = items[middle] as PanelItem<T>;
    if (item.top + item.height <= scrollTop) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  let end = low;
  while (end < items.length && (items[end] as PanelItem<T>).top < scrollTop + viewport) {
    end += 1;
  }
  return items.slice(Math.max(0, low - overscan), Math.min(items.length, end + overscan));
}

/**
 * The scroll that shows a place's row with the least movement; the group's heading comes with the group's first row.
 *
 * @param model The model.
 * @param key The place's key.
 * @param scrollTop How far the panel is scrolled now.
 * @param viewport The viewport's height.
 * @returns The scroll to set, the same one when the row is shown or the key is not in the model.
 */
export function scrollFor<T extends PanelEntry>(model: PanelModel<T>, key: string, scrollTop: number, viewport: number): number {
  const row = model.index.get(key);
  if (row === undefined) {
    return scrollTop;
  }
  const at = model.items.indexOf(row);
  const before = at > 0 ? model.items[at - 1] : undefined;
  const top = before !== undefined && before.type === "heading" ? before.top : row.top;
  if (top < scrollTop) {
    return top;
  }
  if (row.top + row.height > scrollTop + viewport) {
    return row.top + row.height - viewport;
  }
  return scrollTop;
}

/**
 * How far the panel was scrolled, and the filter text it was scrolled under.
 */
export interface PanelScroll {
  readonly top: number;
  readonly filter: string;
}

/**
 * How far the list counts as scrolled under a filter: the kept distance only while the filter is the one it was kept
 * under, else the top, so typing in the filter returns the list to its first row.
 *
 * @param scroll The kept scroll.
 * @param filter The filter text now.
 * @returns The scroll distance in logical pixels.
 */
export function scrollUnder(scroll: PanelScroll, filter: string): number {
  return scroll.filter === filter ? scroll.top : 0;
}
